/**
 * Qué espacios del menú están desplegados.
 *
 * Dos orígenes que se suman: lo que la persona abrió (se recuerda en el
 * navegador) y el espacio de la entrada activa, que se abre solo en cada
 * navegación sin cerrar los demás y sin guardarse. Si el navegador no deja
 * guardar, el menú anda igual y queda abierto solo el de la pantalla actual.
 */
import { useCallback, useState } from 'react';

export const CLAVE_ESPACIOS_ABIERTOS = 'core.nav.espacios-abiertos';

function leer(): string[] {
  try {
    const crudo = window.localStorage.getItem(CLAVE_ESPACIOS_ABIERTOS);
    const valor: unknown = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(valor) ? valor.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function guardar(ids: string[]) {
  try {
    window.localStorage.setItem(CLAVE_ESPACIOS_ABIERTOS, JSON.stringify(ids));
  } catch {
    // Sin almacenamiento (modo privado, cuota): el estado queda solo en memoria.
  }
}

export function useEspaciosAbiertos(espacioActivo: string | null) {
  const [elegidos, setElegidos] = useState<string[]>(leer);
  const [automaticos, setAutomaticos] = useState<string[]>(espacioActivo ? [espacioActivo] : []);
  const [ultimoActivo, setUltimoActivo] = useState<string | null>(espacioActivo);

  // Ajuste durante el render (no en un efecto): al cambiar de espacio activo se
  // suma el nuevo a los automáticos, sin tocar lo que la persona abrió.
  if (espacioActivo !== ultimoActivo) {
    setUltimoActivo(espacioActivo);
    if (espacioActivo && !automaticos.includes(espacioActivo)) {
      setAutomaticos([...automaticos, espacioActivo]);
    }
  }

  const estaAbierto = useCallback(
    (id: string) => elegidos.includes(id) || automaticos.includes(id),
    [elegidos, automaticos],
  );

  const alternar = useCallback(
    (id: string) => {
      if (elegidos.includes(id) || automaticos.includes(id)) {
        const siguiente = elegidos.filter((e) => e !== id);
        setElegidos(siguiente);
        setAutomaticos(automaticos.filter((a) => a !== id));
        guardar(siguiente);
      } else {
        const siguiente = [...elegidos, id];
        setElegidos(siguiente);
        guardar(siguiente);
      }
    },
    [elegidos, automaticos],
  );

  return { estaAbierto, alternar };
}
